import { Link } from "react-router-dom";
import { ArrowLeft, Languages } from "lucide-react";
import { Reveal, PageHero } from "@/components/Motion";
import { CONTACTS } from "@/data/content";
import { useLanguage } from "@/i18n";

const Section = ({ num, title, children }) => (
  <Reveal>
    <section data-testid={`termini-section-${num}`} className="border-t border-stone-300 pt-10">
      <div className="flex items-baseline gap-5">
        <span className="font-mono text-xs tracking-[0.25em] text-[#8F6B3C]">{num}</span>
        <h2 className="font-serif text-2xl uppercase tracking-wide text-stone-900 md:text-3xl">
          {title}
        </h2>
      </div>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-stone-500 md:text-base">
        {children}
      </div>
    </section>
  </Reveal>
);

export default function Termini() {
  const { t, lang } = useLanguage();

  return (
    <main data-testid="termini-page">
      <PageHero
        eyebrow={t("privacyEyebrow")}
        lines={[t("terminiL1"), t("terminiL2")]}
        sub={t("terminiSub")}
      />

      <div className="mx-auto max-w-4xl space-y-14 px-6 pb-24 md:pb-32">
        {lang === "en" && (
          <Reveal>
            <div data-testid="termini-en-note" className="border border-[#C89D66]/40 bg-[#C89D66]/5 p-6 md:p-8">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8F6B3C]">
                <Languages className="h-4 w-4" />
                {t("legalEnNoteTitle")}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-600">{t("legalEnNoteText")}</p>
            </div>
          </Reveal>
        )}

        <Section num="01" title="Oggetto del sito">
          <p>
            Il presente sito web è di proprietà di{" "}
            <strong className="text-stone-800">{CONTACTS.legalName}</strong> · P.IVA {CONTACTS.piva},
            con sede in {CONTACTS.address} · PEC: {CONTACTS.pec}.
          </p>
          <p>
            Il sito ha finalità esclusivamente informative e promozionali: presenta i servizi, i
            progetti e i recapiti dell'impresa. L'accesso e la navigazione comportano
            l'accettazione dei presenti Termini e Condizioni.
          </p>
        </Section>

        <Section num="02" title="Proprietà intellettuale">
          <p>
            Tutti i contenuti del sito — testi, fotografie, grafica, logo e marchio
            "Edilcomplex2020" — sono di proprietà esclusiva di {CONTACTS.legalName} o utilizzati
            con regolare diritto, e sono protetti dalla normativa sul diritto d'autore.
          </p>
          <p>
            È vietata qualsiasi riproduzione, distribuzione o utilizzo dei contenuti, anche
            parziale, senza preventiva autorizzazione scritta del titolare.
          </p>
        </Section>

        <Section num="03" title="Contenuti e preventivi">
          <p>
            Le informazioni pubblicate hanno carattere generale e non costituiscono offerta
            contrattuale. Le richieste inviate tramite il modulo di contatto o WhatsApp non sono
            vincolanti: ogni preventivo ha validità esclusivamente dopo conferma scritta da parte
            dell'impresa.
          </p>
          <p>
            {CONTACTS.legalName} si impegna a mantenere le informazioni aggiornate, ma non garantisce
            l'assenza di errori e si riserva il diritto di modificare i contenuti in qualsiasi
            momento senza preavviso.
          </p>
        </Section>

        <Section num="04" title="Limitazione di responsabilità">
          <p>
            {CONTACTS.legalName} non è responsabile per eventuali danni diretti o indiretti
            derivanti dall'uso del sito o dall'impossibilità temporanea di accedervi, né per i
            contenuti di siti esterni raggiungibili tramite link (WhatsApp, Instagram, Google
            Maps), gestiti autonomamente dai rispettivi titolari.
          </p>
        </Section>

        <Section num="05" title="Legge applicabile e foro competente">
          <p>
            I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia
            relativa all'uso del sito è competente in via esclusiva il Foro di Lucca, salvo
            l'applicazione di norme inderogabili a tutela del consumatore.
          </p>
        </Section>

        <Section num="06" title="Modifiche ai presenti termini">
          <p>
            {CONTACTS.legalName} si riserva il diritto di aggiornare i presenti Termini e
            Condizioni in qualsiasi momento. La versione pubblicata su questa pagina è quella
            vigente. Per informazioni:{" "}
            <a href={`mailto:${CONTACTS.email}`} className="text-[#8F6B3C] hover:underline">{CONTACTS.email}</a>.
          </p>
        </Section>

        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
            {t("lastUpdate")}
          </p>
        </Reveal>

        <Reveal>
          <Link
            data-testid="termini-back-home"
            to="/"
            className="group inline-flex items-center gap-3 border border-stone-300 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t("backHome")}
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
